import config from "appConfig";

const theory = ` <h3 class=sectionHead>Introduction</h3>
<p>Previous Lessons have been devoted to the modelling of electromechanical transducers. We will now look at electroacoustic transducers starting with microphones. The acousto-electrical transduction in microphones takes place in two steps: a first acousto-mechanical transduction and a second mechano-electrical transduction. It is the acousto-mechanical transduction that is responsible for the directivity of microphones. Before building a complete model, we will look in this Lesson at the interaction between the incident sound wave and the microphone membrane. We will pay particular attention to the effect of this interaction on directivity [1, 2, 3].
<h3 class=sectionHead>Membrane size and off-axis coloration</h3>
<p>
   As air is electrically neutral, the acoustic wave which causes particle vibration cannot be directly converted into electricity. Most microphones therefore use a diaphragm to convert the acoustic wave into a movement of solid parts. The vibration of the diaphragm is then converted into electricity by a transduction system (dynamic, electrostatic, piezoelectric, etc.).<br class=newline>Consider a plane wave impinging a diaphragm of surface
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
   </math>
   at normal incidence, a uniform pressure
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>p</mi>
   </math>
   is then created on it. The force acting on the front side of the diaphragm is given by
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi>p</mi>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
   </math>. At oblique incidence, the pressure on the diaphragm is not uniform anymore due to the phase shifts caused by propagation. When the plane wave is reaching the centre of the membrane, a path of length
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>δ</mi>
      <mi>l</mi>
   </math>
   remains to reach the farthest edge (see <a href="#" data-img-src=${config.ASSETS_URL}/04/14/theory/figure-1.svg data-img-alt="Figure 1. Plane wave impinging a diaphragm at oblique incidence.">Figure 1</a>). Similarly, there is a shorter path that leads to the closest edge. The force acting on the membrane is then lower than for the normal incidence case, thus creating an angle of incidence dependence.<br class=newline>
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/14/theory/figure-1.svg alt="Figure 1. Plane wave impinging a diaphragm at oblique incidence."><figcaption class=caption>Figure 1. Plane wave impinging a diaphragm at oblique incidence.</figcaption></figure>
<p>
   At low frequencies (<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>δ</mi>
      <mi>l</mi>
      <mo class=MathClass-rel>≪</mo>
   </math>
   wavelength
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>λ</mi>
   </math>), these differences become negligible: the pressure can be considered homogeneous on the diaphragm. This is the reason why small diaphragms allows to reduce the angle dependence.<br class=newline>
<p>Using a small microphone can also reduce the effects of intrusion into the sound field. Here also, as long as the dimensions of the microphone are small compared to the wavelength, the pressure in the presence of the microphone is very close to that which would have been observed in its absence.<br class=newline>For these two reasons, measurement microphones generally have small diaphragms, especially if high frequencies are to be captured. Note that a small diaphragm surface area reduces the force acting on the diaphragm and thus the sensitivity of the microphone. There is therefore a trade-off between reducing the effects of microphone intrusion and the angle of incidence in respect of sensitivity.<br class=newline>
<h3 class=subsectionHead>Acousto-mechanical transduction principles</h3>   
<p>As the membrane has two sides, the total force acting on the membrane depends on the forces applied on each side. The following two cases can be distinguished:
<ul class=itemize1>
   <li class=itemize>the case where the rear side is isolated from the sound pressure,
   <li class=itemize>the case where the acoustic wave has access to both sides.
</ul>
<p>These two configurations are studied below.
<h4 class=subsectionHead>Pressure microphone</h4>
<p>
   <a href="#" data-img-src=${config.ASSETS_URL}/04/14/theory/figure-2.svg data-img-alt="Figure 2. Plane wave impinging a pressure microphone.">Figure 2</a> shows a microphone for which the acoustic wave pressure cannot be applied to the diaphragm rear side as its access is blocked by the housing. There is however an opening which allows to equalise the static pressure on either side of the diaphragm, but its diameter is small enough that its effect is negligible for the frequencies belonging to the audible frequency range.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/14/theory/figure-2.svg alt="Figure 2. Plane wave impinging a pressure microphone."><figcaption class=caption>Figure 2. Plane wave impinging a pressure microphone.</figcaption></figure>
<p>At low frequencies, the pressure applied to the front of the diaphragm by the acoustic wave is homogeneous and identical for all angles of incidence as long as the wavelength is large compared to the dimensions of the microphone (phase shifts are negligible, see above. The response does not then depend on the angle of incidence. The microphone is said to be omnidirectional.
<h4 class=subsectionHead>Pressure-gradient microphone (bidirectional)</h4>
<p>
   Here also, for low frequencies the pressures acting on each face of the membrane are homogeneous. The resulting force is then equal to
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi>S</mi>
      <mo class=MathClass-open>(</mo>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-bin>−</mo>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
      <mo class=MathClass-close>)</mo>
   </math>, where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   is the pressure on the front face and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
   is the pressure on the rear face (see <a href="#" data-img-src=${config.ASSETS_URL}/04/14/theory/figure-3.svg data-img-alt="Figure 3. Plane wave impinging a pressure-gradient microphone.">Figure 3</a>).
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/14/theory/figure-3.svg alt="Figure 3. Plane wave impinging a pressure-gradient microphone."><figcaption class=caption>Figure 3. Plane wave impinging a pressure-gradient microphone.</figcaption></figure>
<p>
   The path difference between two points on either side of the membrane is equal to
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>δ</mi>
      <mi>r</mi>
      <mo class=MathClass-rel>=</mo>
      <mi>e</mi>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
   </math>. Then,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
         </mrow>
      </msub>
   </math>
   can be rewritten
<table class=equation>
   <tr>
      <td style="font-size: 12px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>F</mi>
               </mrow>
               <mrow>
                  <mi>a</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mi>S</mi>
            <mo class=MathClass-open>(</mo>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msub>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mi>S</mi>
            <mi class=MathClass-op>∇</mi>
            <mo>⁡</mo>
            <mi>p</mi>
            <mi>δ</mi>
            <mi>r</mi>
            <mo class=MathClass-rel>=</mo>
            <mi>e</mi>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mi>𝜃</mi>
            <mi>S</mi>
            <mi class=MathClass-op>∇</mi>
            <mo>⁡</mo>
            <mi>p</mi>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   By using Euler’s equation (see Acoustic fundamentals course) for the harmonic regime,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi class=MathClass-op>∇</mi>
      <mo>⁡</mo>
      <mi>p</mi>
      <mo class=MathClass-rel>=</mo>
      <mi>j</mi>
      <mi>ω</mi>
      <msub>
         <mrow>
            <mi>ρ</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mi>V</mi>
   </math>, we get
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>F</mi>
               </mrow>
               <mrow>
                  <mi>a</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mi>j</mi>
            <mi>ω</mi>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>e</mi>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mi>𝜃</mi>
            <mi>S</mi>
            <mi>V</mi>
         </math>
      <td class=eq-no>(2)
</table>
<p>
   As
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
         </mrow>
      </msub>
   </math>
   is proportional to the particle velocity, the pressure-gradient microphone is also called a velocity microphone.<br class=newline>
<p>
   For a plane wave excitation:
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>p</mi>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>ρ</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mi>c</mi>
      <mi>V</mi>
   </math>, we have
<table class=equation>
   <tr>
      <td style="font-size: 12px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>F</mi>
               </mrow>
               <mrow>
                  <mi>a</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mi>j</mi>
            <mi>ω</mi>
            <msub>
              <mrow>
                  <mi>ρ</mi>
              </mrow>
              <mrow>
                  <mn>0</mn>
              </mrow>
            </msub>
         <mi>e</mi>
         <mi class=qopname>cos</mi>
         <mo>⁡</mo>
         <mi>𝜃</mi>
         <mi>S</mi>
         <mfrac>
            <mrow>
               <mi>p</mi>
            </mrow>
            <mrow>
            <msub>
                <mrow>
                  <mi>ρ</mi>
                </mrow>
                <mrow>
                  <mn>0</mn>
                </mrow>
            </msub>
               <mi>c</mi>
            </mrow>
         </mfrac>
         <mo class=MathClass-rel>=</mo>
         <mi>j</mi>
         <mfrac>
            <mrow>
               <mi>ω</mi>
            </mrow>
            <mrow>
               <mi>c</mi>
            </mrow>
         </mfrac>
         <mi>e</mi>
         <mi class=qopname>cos</mi>
         <mo>⁡</mo>
         <mi>𝜃</mi>
         <mi>S</mi>
         <mi>p</mi>
         </math>
      <td class=eq-no>(3)
</table>
<p>
   To sum up,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
         </mrow>
      </msub>
   </math>
   is proportional to:
<ul class=itemize1>
   <li class=itemize>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi class=qopname>cos</mi>
         <mo>⁡</mo>
         <mi>𝜃</mi>
      </math>
      , the response will be maximum on axis and null for
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi>𝜃</mi>
         <mo class=MathClass-rel>=</mo>
         <mo class=MathClass-bin>±</mo>
         <mi>π</mi>
         <mo class=MathClass-bin>∕</mo>
         <mn>2</mn>
      </math>. This microphone is bidirectional,
   <li class=itemize>
      the frequency
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi>f</mi>
      </math>, which gives
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mo class=MathClass-bin>+</mo>
         <mn>6</mn>
      </math>
      dB/octave slope for the response curve. The increase of
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>F</mi>
            </mrow>
            <mrow>
               <mi>a</mi>
            </mrow>
         </msub>
      </math>
      will have to be compensated by the transduction process to get a flat curve,
   <li class=itemize>
      the parameter
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi>e</mi>
      </math>, corresponds to the contour distance between the two sides and is generally greater than the actual membrane thickness due to the microphone body. Note that 
            <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi>e</mi>
      </math>
              must remain small compared to the wavelength in order to avoid oscillations in the frequency response curve (when
       <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi>e</mi>
      </math>
       is equal to a multiple of 
          <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>λ</mi>
   </math>
the acoustic pressures on the front and rear diaphragm sides are equal and 
             <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>F</mi>
            </mrow>
            <mrow>
               <mi>a</mi>
            </mrow>
         </msub>
      </math>
 is then null).
</ul>
<p>
   Directivity patterns of the pressure and pressure-gradient microphones are plotted in <a href="#" data-img-src=${config.ASSETS_URL}/04/14/theory/figure-4.svg data-img-alt="Figure 4. Directivity of a pressure microphone (left) and a pressure-gradient (also called “figure-of-eight”) microphone (right).">Figure 4</a>.
   <figure class=figure>
      <img src=${config.ASSETS_URL}/04/14/theory/figure-4.svg alt="Figure 4. Directivity of a pressure microphone (left) and a pressure-gradient (also called “figure-of-eight”) microphone (right).">
      <figcaption class=caption>Figure 4. Directivity of a pressure microphone (left) and a pressure-gradient (also called “figure-of-eight”) microphone (right).</figcaption>
   </figure>
<p>When the source is close to the diaphragm, the incident sound can be described by a spherical wave. In this case the velocity field is given by:</p>
<math xmlns=http://www.w3.org/1998/Math/MathML display=block>
   <mi>V</mi>
   <mo class=MathClass-rel>=</mo>
   <mfrac>
      <mrow>
         <msub>
            <mrow>
               <mi>p</mi>
            </mrow>
            <mrow>
               <mn>1</mn>
            </mrow>
         </msub>
      </mrow>
      <mrow>
         <msub>
            <mrow>
               <mi>ρ</mi>
            </mrow>
            <mrow>
               <mn>0</mn>
            </mrow>
         </msub>
         <mi>c</mi>
      </mrow>
   </mfrac>
   <mrow>
      <mo fence=true form=prefix>(</mo>
      <mrow>
         <mn>1</mn>
         <mo class=MathClass-bin>+</mo>
         <mfrac>
            <mrow>
               <mn>1</mn>
            </mrow>
            <mrow>
               <mi>j</mi>
               <mi>k</mi>
               <mi>r</mi>
            </mrow>
         </mfrac>
      </mrow>
      <mo fence=true form=postfix>)</mo>
   </mrow>
</math>
<p>leading to the following force acting on the membrane:</p>
<span style="font-size: 11px">
<math xmlns=http://www.w3.org/1998/Math/MathML display=block>
   <msub>
      <mrow>
         <mi>F</mi>
      </mrow>
      <mrow>
         <mi>a</mi>
      </mrow>
   </msub>
   <mo class=MathClass-rel>=</mo>
   <mi>j</mi>
   <mi>ω</mi>
   <msub>
      <mrow>
         <mi>ρ</mi>
      </mrow>
      <mrow>
         <mn>0</mn>
      </mrow>
   </msub>
   <mi>e</mi>
   <mi class=qopname>cos</mi>
   <mo>⁡</mo>
   <mi>𝜃</mi>
   <mi>S</mi>
   <mi>V</mi>
   <mo class=MathClass-rel>=</mo>
   <mi>j</mi>
   <mi>k</mi>
   <mi>e</mi>
   <mi class=qopname>cos</mi>
   <mo>⁡</mo>
   <mi>𝜃</mi>
   <mi>S</mi>
   <mrow>
      <mo fence=true form=prefix>(</mo>
      <mrow>
         <mn>1</mn>
         <mo class=MathClass-bin>+</mo>
         <mfrac>
            <mrow>
               <mn>1</mn>
            </mrow>
            <mrow>
               <mi>j</mi>
               <mi>k</mi>
               <mi>r</mi>
            </mrow>
         </mfrac>
      </mrow>
      <mo fence=true form=postfix>)</mo>
   </mrow>
   <mi>p</mi>
</math>
</span>
<p>
   At low frequencies,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>≃</mo>
      <mi>e</mi>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
      <mi>S</mi>
      <mfrac>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <mi>r</mi>
         </mrow>
      </mfrac>
      <mi>p</mi>
   </math>, giving a flat frequency response. At high frequencies, the plane wave behaviour is recovered with a
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-bin>+</mo>
      <mn>6</mn>
   </math>
   dB/octave slope. <a href="#" data-img-src=${config.ASSETS_URL}/04/14/theory/figure-5.svg data-img-alt="Figure 5. Proximity effect.">Figure 5</a> shows the shape of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
         </mrow>
      </msub>
   </math>
   for a close and a distant source. The low frequency content of a nearby source will therefore be emphasised over that of a distant source. This gradient-pressure microphone property is often used for the discrimination of speech signal in a noisy environment.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/14/theory/figure-5.svg alt="Figure 5. Proximity effect."><figcaption class=caption>Figure 5. Proximity effect.</figcaption></figure>
<h4 class=sectionHead>Combined pressure and gradient pressure microphones (unidirectional)</h4>
<p>Unidirectional microphones combine omnidirectional and bidirectional patterns. If pressure and pressure-gradient microphones are mixed in equal parts, the following directivity function is obtained:</p>
<math xmlns=http://www.w3.org/1998/Math/MathML display=block>
   <mi>d</mi>
   <mo class=MathClass-open>(</mo>
   <mi>𝜃</mi>
   <mo class=MathClass-close>)</mo>
   <mo class=MathClass-rel>=</mo>
   <mfrac>
      <mrow>
         <mn>1</mn>
         <mo class=MathClass-bin>+</mo>
         <mi class=qopname>cos</mi>
         <mo>⁡</mo>
         <mi>𝜃</mi>
      </mrow>
      <mrow>
         <mn>2</mn>
      </mrow>
   </mfrac>
</math>
<p>
   which is plotted in the upper left part of <a href="#" data-img-src=${config.ASSETS_URL}/04/14/theory/figure-6.svg data-img-alt="Figure 6. Directivity patterns of unidirectional microphones.">Figure 6</a>. This pattern is called cardioid. It can be observed that the response of the microphone is maximal in the main direction and then decreases to zero for
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>𝜃</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>1</mn>
      <mn>8</mn>
      <msup>
         <mrow>
            <mn>0</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>∘</mo>
         </mrow>
      </msup>
   </math>. It is possible to build other directivity patterns by changing the respective amounts of the omnidirectional and bidirectional components:
</p>
<math xmlns=http://www.w3.org/1998/Math/MathML display=block>
   <mi>d</mi>
   <mo class=MathClass-open>(</mo>
   <mi>𝜃</mi>
   <mo class=MathClass-close>)</mo>
   <mo class=MathClass-rel>=</mo>
   <mn>1</mn>
   <mo class=MathClass-bin>−</mo>
   <mi>β</mi>
   <mo class=MathClass-bin>+</mo>
   <mi>β</mi>
   <mi class=qopname>cos</mi>
   <mo>⁡</mo>
   <mi>𝜃</mi>
</math>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>0</mn>
      <mo class=MathClass-rel>≤</mo>
      <mi>β</mi>
      <mo class=MathClass-rel>≤</mo>
      <mn>1</mn>
   </math>
   is the pattern coefficient. A
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>β</mi>
   </math>
   value of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>0</mn>
   </math>
   corresponds to an omnidirectional directivity while a value of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>1</mn>
   </math>
   corresponds to a bidirectional directivity, the intermediate values allow for a wide range of unidirectional patterns.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/14/theory/figure-6.svg alt="Figure 6. Directivity patterns of unidirectional microphones."><figcaption class=caption>Figure 6. Directivity patterns of unidirectional microphones.</figcaption></figure>
<p>
   The list of common patterns name associated to their
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>β</mi>
   </math>
   value is given in <a href="#" data-img-src=${config.ASSETS_URL}/04/14/theory/table-1.svg data-img-alt="Table 1. Common directivity patterns.">Table 1</a> and some directivity patterns are plotted in <a href="#" data-img-src=${config.ASSETS_URL}/04/14/theory/figure-6.svg data-img-alt="Figure 6. Directivity patterns of unidirectional microphones.">Figure 6</a>. The effect of the
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>β</mi>
   </math>
   parameter on the directivity can be observed.<br class=newline>
<div class=table>
   <figure class=float>
      <figcaption class=caption>Table 1. Common directivity patterns.</figcaption>
      <img src=${config.ASSETS_URL}/04/14/theory/table-1.svg alt="Table 1. Common directivity patterns.">
   </figure>
</div>
<p>
   Unidirectional microphone of higher order can also be used. The directivity pattern for order
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>n</mi>
   </math>
   is given by:
</p>
<span style="font-size: 13px">
<math xmlns=http://www.w3.org/1998/Math/MathML display=block>
   <mi>d</mi>
   <mo class=MathClass-open>(</mo>
   <mi>𝜃</mi>
   <mo class=MathClass-close>)</mo>
   <mo class=MathClass-rel>=</mo>
   <mo class=MathClass-open>(</mo>
   <mo class=MathClass-open>(</mo>
   <mn>1</mn>
   <mo class=MathClass-bin>−</mo>
   <mi>β</mi>
   <mo class=MathClass-close>)</mo>
   <mo class=MathClass-bin>+</mo>
   <mi>β</mi>
   <mi class=qopname>cos</mi>
   <mo>⁡</mo>
   <mi>𝜃</mi>
   <mo class=MathClass-close>)</mo>
   <msup>
      <mrow>
         <mo class=MathClass-open>(</mo>
         <mi class=qopname>cos</mi>
         <mo>⁡</mo>
         <mi>𝜃</mi>
         <mo class=MathClass-close>)</mo>
      </mrow>
      <mrow>
         <mi>n</mi>
         <mo class=MathClass-bin>−</mo>
         <mn>1</mn>
      </mrow>
   </msup>
</math>
</span>
<p>The directivity pattern for a cardioid microphone of order 2 is given in the bottom right plot of <a href="#" data-img-src=${config.ASSETS_URL}/04/14/theory/figure-6.svg data-img-alt="Figure 6. Directivity patterns of unidirectional microphones.">Figure 6</a>.<br class=newline>In practice, unidirectional directivities can be achieved by various means:
<ul class=itemize1>
   <li class=itemize>a part of the membrane is sensitive to the pressure, the other part to the pressure gradient, this system is however difficult to build,
   <li class=itemize>the diaphragm is directly excited by the sound wave on its front face and via an acoustical circuit that controls the amplitude and phase shift on the rear face,
   <li class=itemize>two pressure microphones separated by a small distance are used. Then, the summation of the electrical signals coming from the two microphones is done using filters calculated to get the targeted directivity pattern. A larger number of microphones is required to get higher order shapes, <em><span class=cmti-10>e.g.</span></em> 3 microphones for an order 2 pattern.
</ul>
<h3 class=sectionHead>Highly directional microphones</h3>
<p>
   Other techniques can be used to get very sharp directivity patterns. For instance, a microphone can be placed at the focal point of a parabolic reflector (see <a href="#" data-img-src=${config.ASSETS_URL}/04/14/theory/figure-7.svg data-img-alt="Figure 7. Parabolic microphone principle.">Figure 7</a>). Then, all sound rays reflected on the parabola will sum in phase on the microphone for on-axis incidence. For other directions, destructive interference will lower the pressure on the microphone. Due to their generally low quality, parabolic microphones are not used for High Fidelity (HiFi) applications but for animal recordings or sport broadcasting.<br class=newline>
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/14/theory/figure-7.svg alt="Figure 7. Parabolic microphone principle."><figcaption class=caption>Figure 7. Parabolic microphone principle.</figcaption></figure>
<p>Shotgun microphones can also be used to achieve high directivity. A multi-aperture tube is placed on the front of a pressure microphone. Destructive interference will lower the response for off-axis incidence, while constructive interference will favour on-axis incidence.
<h3 class=sectionHead>Conclusion</h3>
<p>In this Lesson, the interaction of a sound wave with both sides of the diaphragm of a microphone was examined with regard to the influence on directivity. These results will be used in a future Lesson dedicated to the modelling of a unidirectional electrodynamic microphone.
<h3 class=likesectionHead>Glossary</h3>
<dl>
  <dt>HiFi
  <dd>High Fidelity
</dl>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt id=X0-Beranek2012 class=thebibliography>[1]
   <dd id=bib-1 class=thebibliography>
      <p>Leo Beranek and Tim Mellow. <span class=cmti-10>Acoustics: sound fields and transducers</span>. Academic Pres, 2012. <span class=cmcsc-10><span class=small-caps>i</span><span class=small-caps>s</span><span class=small-caps>b</span><span class=small-caps>n</span></span>: 9780123914866.
   <dt id=X0-Kleiner2013 class=thebibliography>[2]
   <dd id=bib-2 class=thebibliography>
      <p>Mendel Kleiner. <span class=cmti-10>Electroacoustics</span>. CRC Press, 2013. <span class=cmcsc-10><span class=small-caps>i</span><span class=small-caps>s</span><span class=small-caps>b</span><span class=small-caps>n</span></span>: 9780429073519.
   <dt id=X0-Rossi1988 class=thebibliography>[3]
   <dd id=bib-3 class=thebibliography>
      <p>Mario Rossi. <span class=cmti-10>Acoustics and Electroacoustics</span>. Artech House, 1988. <span class=cmcsc-10><span class=small-caps>i</span><span class=small-caps>s</span><span class=small-caps>b</span><span class=small-caps>n</span></span>: 978-0890062555.
</dl>`;

export default theory;
